import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Download, Upload, RotateCcw, Maximize2, Minimize2, BarChart3, FileText, Zap, CheckCircle2 } from 'lucide-react';

interface ProcessingStats {
  charactersProcessed: number;
  entitiesFound: number;
  processingTime: number;
  operationsCount: number;
}

interface ProcessingResult {
  result: string;
  stats: Omit<ProcessingStats, 'operationsCount'>;
}

const PythonEscapeUnescapeTool: React.FC = () => {
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  const [mode, setMode] = useState<'escape' | 'unescape'>('escape');
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [stats, setStats] = useState<ProcessingStats>({
    charactersProcessed: 0,
    entitiesFound: 0,
    processingTime: 0,
    operationsCount: 0
  });
  const [notification, setNotification] = useState<string>('');

  const escapePython = useCallback((text: string): ProcessingResult => {
    const startTime = performance.now();
    let result = text;
    let entitiesCount = 0;

    // Escape backslashes first to avoid conflicts
    const backslashMatches = result.match(/\\/g);
    if (backslashMatches) {
      entitiesCount += backslashMatches.length;
      result = result.replace(/\\/g, '\\\\');
    }

    // Escape double quotes
    const doubleQuoteMatches = result.match(/"/g);
    if (doubleQuoteMatches) {
      entitiesCount += doubleQuoteMatches.length;
      result = result.replace(/"/g, '\\"');
    }

    // Escape single quotes
    const singleQuoteMatches = result.match(/'/g);
    if (singleQuoteMatches) {
      entitiesCount += singleQuoteMatches.length;
      result = result.replace(/'/g, "\\'");
    }

    // Escape newlines
    const newlineMatches = result.match(/\n/g);
    if (newlineMatches) {
      entitiesCount += newlineMatches.length;
      result = result.replace(/\n/g, '\\n');
    }

    // Escape carriage returns
    const carriageReturnMatches = result.match(/\r/g);
    if (carriageReturnMatches) {
      entitiesCount += carriageReturnMatches.length;
      result = result.replace(/\r/g, '\\r');
    }

    // Escape tabs
    const tabMatches = result.match(/\t/g);
    if (tabMatches) {
      entitiesCount += tabMatches.length;
      result = result.replace(/\t/g, '\\t');
    }

    // Escape backspace
    const backspaceMatches = result.match(/\b/g);
    if (backspaceMatches) {
      entitiesCount += backspaceMatches.length;
      result = result.replace(/\b/g, '\\b');
    }

    // Escape form feed
    const formFeedMatches = result.match(/\f/g);
    if (formFeedMatches) {
      entitiesCount += formFeedMatches.length;
      result = result.replace(/\f/g, '\\f');
    }

    // Escape vertical tab
    const verticalTabMatches = result.match(/\v/g);
    if (verticalTabMatches) {
      entitiesCount += verticalTabMatches.length;
      result = result.replace(/\v/g, '\\v');
    }

    // Escape null character
    const nullMatches = result.match(/\0/g);
    if (nullMatches) {
      entitiesCount += nullMatches.length;
      result = result.replace(/\0/g, '\\0');
    }

    // Escape bell character
    const bellMatches = result.match(/\x07/g);
    if (bellMatches) {
      entitiesCount += bellMatches.length;
      result = result.replace(/\x07/g, '\\a');
    }

    // Handle other control characters with hex notation
    result = result.replace(/[\x01-\x06\x0E-\x1F\x7F]/g, (match) => {
      const code = match.charCodeAt(0);
      entitiesCount++;
      return `\\x${code.toString(16).padStart(2, '0')}`;
    });

    // Handle extended ASCII and Unicode characters
    result = result.replace(/[\x80-\xFF]/g, (match) => {
      const code = match.charCodeAt(0);
      entitiesCount++;
      return `\\x${code.toString(16).padStart(2, '0')}`;
    });

    // Handle Unicode characters above 255
    result = result.replace(/[\u0100-\uFFFF]/g, (match) => {
      const code = match.charCodeAt(0);
      entitiesCount++;
      return `\\u${code.toString(16).padStart(4, '0')}`;
    });

    // Handle surrogate pairs for characters above BMP
    result = result.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, (match) => {
      const high = match.charCodeAt(0);
      const low = match.charCodeAt(1);
      // Convert surrogate pair back to code point
      const codePoint = 0x10000 + ((high & 0x3FF) << 10) + (low & 0x3FF);
      entitiesCount++;
      return `\\U${codePoint.toString(16).padStart(8, '0')}`;
    });

    const endTime = performance.now();
    return {
      result,
      stats: {
        charactersProcessed: text.length,
        entitiesFound: entitiesCount,
        processingTime: endTime - startTime
      }
    };
  }, []);

  const unescapePython = useCallback((text: string): ProcessingResult => {
    const startTime = performance.now();
    let result = text;
    let entitiesCount = 0;

    // Unescape Unicode sequences (\UXXXXXXXX) - 32-bit
    result = result.replace(/\\U([0-9a-fA-F]{8})/g, (match, hex) => {
      const code = parseInt(hex, 16);
      if (code >= 0 && code <= 0x10FFFF) {
        entitiesCount++;
        if (code <= 0xFFFF) {
          return String.fromCharCode(code);
        } else {
          // Convert to surrogate pair
          const high = Math.floor((code - 0x10000) / 0x400) + 0xD800;
          const low = ((code - 0x10000) % 0x400) + 0xDC00;
          return String.fromCharCode(high) + String.fromCharCode(low);
        }
      }
      return match;
    });

    // Unescape Unicode sequences (\uXXXX) - 16-bit
    result = result.replace(/\\u([0-9a-fA-F]{4})/g, (match, hex) => {
      const code = parseInt(hex, 16);
      if (code >= 0 && code <= 65535) {
        entitiesCount++;
        return String.fromCharCode(code);
      }
      return match;
    });

    // Unescape hex sequences (\xXX)
    result = result.replace(/\\x([0-9a-fA-F]{2})/g, (match, hex) => {
      const code = parseInt(hex, 16);
      if (code >= 0 && code <= 255) {
        entitiesCount++;
        return String.fromCharCode(code);
      }
      return match;
    });

    // Unescape octal sequences (\ooo) - Python supports up to \377
    result = result.replace(/\\([0-7]{1,3})/g, (match, oct) => {
      const code = parseInt(oct, 8);
      if (code >= 0 && code <= 255 && oct !== '0') {
        entitiesCount++;
        return String.fromCharCode(code);
      }
      return match;
    });

    // Unescape named character sequences
    result = result.replace(/\\N\{([^}]+)\}/g, (match, name) => {
      // This is a simplified version - in real Python, this would look up Unicode names
      entitiesCount++;
      return match; // Return as-is for display purposes
    });

    // Unescape bell character
    const bellEscapeMatches = result.match(/\\a/g);
    if (bellEscapeMatches) {
      entitiesCount += bellEscapeMatches.length;
      result = result.replace(/\\a/g, '\x07');
    }

    // Unescape backspace
    const backspaceEscapeMatches = result.match(/\\b/g);
    if (backspaceEscapeMatches) {
      entitiesCount += backspaceEscapeMatches.length;
      result = result.replace(/\\b/g, '\b');
    }

    // Unescape form feed
    const formFeedEscapeMatches = result.match(/\\f/g);
    if (formFeedEscapeMatches) {
      entitiesCount += formFeedEscapeMatches.length;
      result = result.replace(/\\f/g, '\f');
    }

    // Unescape newlines
    const newlineEscapeMatches = result.match(/\\n/g);
    if (newlineEscapeMatches) {
      entitiesCount += newlineEscapeMatches.length;
      result = result.replace(/\\n/g, '\n');
    }

    // Unescape carriage returns
    const carriageReturnEscapeMatches = result.match(/\\r/g);
    if (carriageReturnEscapeMatches) {
      entitiesCount += carriageReturnEscapeMatches.length;
      result = result.replace(/\\r/g, '\r');
    }

    // Unescape tabs
    const tabEscapeMatches = result.match(/\\t/g);
    if (tabEscapeMatches) {
      entitiesCount += tabEscapeMatches.length;
      result = result.replace(/\\t/g, '\t');
    }

    // Unescape vertical tabs
    const verticalTabEscapeMatches = result.match(/\\v/g);
    if (verticalTabEscapeMatches) {
      entitiesCount += verticalTabEscapeMatches.length;
      result = result.replace(/\\v/g, '\v');
    }

    // Unescape null character
    const nullEscapeMatches = result.match(/\\0/g);
    if (nullEscapeMatches) {
      entitiesCount += nullEscapeMatches.length;
      result = result.replace(/\\0/g, '\0');
    }

    // Unescape single quotes
    const singleQuoteEscapeMatches = result.match(/\\'/g);
    if (singleQuoteEscapeMatches) {
      entitiesCount += singleQuoteEscapeMatches.length;
      result = result.replace(/\\'/g, "'");
    }

    // Unescape double quotes
    const doubleQuoteEscapeMatches = result.match(/\\"/g);
    if (doubleQuoteEscapeMatches) {
      entitiesCount += doubleQuoteEscapeMatches.length;
      result = result.replace(/\\"/g, '"');
    }

    // Unescape backslashes (must be last)
    const backslashEscapeMatches = result.match(/\\\\/g);
    if (backslashEscapeMatches) {
      entitiesCount += backslashEscapeMatches.length;
      result = result.replace(/\\\\/g, '\\');
    }

    const endTime = performance.now();
    return {
      result,
      stats: {
        charactersProcessed: text.length,
        entitiesFound: entitiesCount,
        processingTime: endTime - startTime
      }
    };
  }, []);

  const processText = useCallback((): void => {
    if (!inputText.trim()) {
      setOutputText('');
      return;
    }

    const processor = mode === 'escape' ? escapePython : unescapePython;
    const { result, stats: processingStats } = processor(inputText);
    
    setOutputText(result);
    setStats(prev => ({
      ...processingStats,
      operationsCount: prev.operationsCount + 1
    }));
  }, [inputText, mode, escapePython, unescapePython]);

  useEffect(() => {
    processText();
  }, [processText]);

  const showNotification = (message: string): void => {
    setNotification(message);
    setTimeout(() => setNotification(''), 3000);
  };

  const copyToClipboard = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(outputText);
      showNotification('Copied to clipboard!');
    } catch (err) {
      showNotification('Failed to copy');
    }
  };

  const downloadFile = (): void => {
    const blob = new Blob([outputText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${mode}d-python.${mode === 'escape' ? 'py' : 'txt'}`;
    a.click();
    URL.revokeObjectURL(url);
    showNotification('File downloaded!');
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          setInputText(result);
          showNotification('File loaded successfully!');
        }
      };
      reader.readAsText(file);
    }
  };

  const clearAll = (): void => {
    setInputText('');
    setOutputText('');
    setStats({
      charactersProcessed: 0,
      entitiesFound: 0,
      processingTime: 0,
      operationsCount: 0
    });
    showNotification('Cleared all content!');
  };

  const toggleFullscreen = (): void => {
    setIsFullscreen(!isFullscreen);
  };

  const loadSamplePython = (): void => {
    setInputText(`# Sample Python code with special characters
def string_escape_example():
    # String literals with special characters
    message = "Hello World!\\nWelcome to Python programming."
    path = "C:\\\\Users\\\\Documents\\\\file.txt"
    quote = 'He said, "Python is amazing!"'
    
    # Raw strings (no escaping needed)
    raw_path = r"C:\\Users\\Documents\\file.txt"
    raw_regex = r"\\d+\\.\\d+"
    
    # Triple quoted strings
    multiline = """Line 1
    Line 2 with "quotes"
    Line 3 with backslashes: \\\\"""
    
    # f-strings (Python 3.6+)
    name = "Alice"
    age = 30
    f_string = f"Name: {name}\\nAge: {age}"
    
    # Bytes literals
    byte_string = b"Hello\\x20World\\xff"
    
    # Unicode characters
    unicode_str = "Unicode: \\u03B1\\u03B2\\u03B3"  # α β γ
    emoji = "\\U0001F600"  # 😀
    chinese = "\\u4E2D\\u6587"  # 中文
    
    # Named Unicode characters
    named_unicode = "\\N{GREEK SMALL LETTER ALPHA}"
    
    # Control characters
    control_chars = "\\a\\b\\f\\r\\t\\v\\0"
    
    # Hex and octal escapes
    hex_char = "\\x41"  # 'A'
    octal_char = "\\101"  # 'A'
    
    # String formatting
    formatted = "Name: %s\\nScore: %.2f%%" % (name, 95.5)
    
    # Regular expressions with escaping
    import re
    regex_pattern = r"\\w+@\\w+\\.\\w+"  # Email pattern
    
    # File paths with different quote styles
    windows_path = "C:\\\\Program Files\\\\Python\\\\"
    unix_path = '/home/user/documents/file.txt'
    
    # JSON string (needs escaping for Python)
    json_str = '{"name": "John", "message": "Hello\\\\nWorld"}'
    
    # SQL query with escaping
    sql_query = "SELECT * FROM users WHERE name = 'O\\'Connor'"
    
    # Docstring with escaping
    def example_function():
        """
        This function demonstrates escaping in docstrings.
        Path: C:\\\\temp\\\\file.txt
        Quote: "Hello World"
        """
        pass
    
    return message

# Class with string constants
class StringConstants:
    ERROR_MSG = "Error: File \\"{}"\\" not found."
    SUCCESS_MSG = 'Operation completed successfully!'
    MULTILINE_MSG = """This is a multiline
    message with "quotes" and
    special characters: \\n\\t\\r"""

# Dictionary with escaped strings
config = {
    'database_url': 'postgresql://user:pass@localhost/db',
    'error_template': 'Error on line {}: "{}"',
    'file_pattern': r'\\w+\\.(py|txt|json)$'
}`);
  };

  const loadSampleEscaped = (): void => {
    setInputText(`# Sample escaped Python code
def string_escape_example():
    # String literals with escaped characters
    message = \\"Hello World!\\\\nWelcome to Python programming.\\"
    path = \\"C:\\\\\\\\Users\\\\\\\\Documents\\\\\\\\file.txt\\"
    quote = \\'He said, \\\\\\"Python is amazing!\\\\\\"!\\'
    
    # Raw strings (no escaping needed)
    raw_path = r\\"C:\\\\\\\\Users\\\\\\\\Documents\\\\\\\\file.txt\\"
    raw_regex = r\\"\\\\\\\\d+\\\\\\\\.\\\\\\\\d+\\"
    
    # Triple quoted strings
    multiline = \\"\\"\\"\\"Line 1
    Line 2 with \\\\\\"quotes\\\\\\"
    Line 3 with backslashes: \\\\\\\\\\\\\\\\\\"\\"\\"\\"
    
    # f-strings (Python 3.6+)
    name = \\"Alice\\"
    age = 30
    f_string = f\\"Name: {name}\\\\\\\\nAge: {age}\\"
    
    # Bytes literals
    byte_string = b\\"Hello\\\\\\\\x20World\\\\\\\\xff\\"
    
    # Unicode characters
    unicode_str = \\"Unicode: \\\\\\\\u03B1\\\\\\\\u03B2\\\\\\\\u03B3\\"  # α β γ
    emoji = \\"\\\\\\\\U0001F600\\"  # 😀
    chinese = \\"\\\\\\\\u4E2D\\\\\\\\u6587\\"  # 中文
    
    # Named Unicode characters
    named_unicode = \\"\\\\\\\\N{GREEK SMALL LETTER ALPHA}\\"
    
    # Control characters
    control_chars = \\"\\\\\\\\a\\\\\\\\b\\\\\\\\f\\\\\\\\r\\\\\\\\t\\\\\\\\v\\\\\\\\0\\"
    
    # Hex and octal escapes
    hex_char = \\"\\\\\\\\x41\\"  # \\'A\\'
    octal_char = \\"\\\\\\\\101\\"  # \\'A\\'
    
    # String formatting
    formatted = \\"Name: %s\\\\\\\\nScore: %.2f%%\\" % (name, 95.5)
    
    # Regular expressions with escaping
    import re
    regex_pattern = r\\"\\\\\\\\w+@\\\\\\\\w+\\\\\\\\.\\\\\\\\w+\\"  # Email pattern
    
    # File paths with different quote styles
    windows_path = \\"C:\\\\\\\\\\\\\\\\Program Files\\\\\\\\\\\\\\\\Python\\\\\\\\\\\\\\\\\\"
    unix_path = \\'/home/user/documents/file.txt\\'
    
    # JSON string (needs escaping for Python)
    json_str = \\'{\\"\\"name\\"\\":\\"\\"John\\"\\"\\",\\"\\"message\\"\\":\\"\\"Hello\\\\\\\\\\\\\\\\nWorld\\"\\"}\\'
    
    # SQL query with escaping
    sql_query = \\"SELECT * FROM users WHERE name = \\'O\\\\\\\\\\'Connor\\'\\"
    
    # Docstring with escaping
    def example_function():
        \\"\\"\\"\\"
        This function demonstrates escaping in docstrings.
        Path: C:\\\\\\\\\\\\\\\\temp\\\\\\\\\\\\\\\\file.txt
        Quote: \\\\\\"Hello World\\\\\\"
        \\"\\"\\"\\"
        pass
    
    return message

# Class with string constants
class StringConstants:
    ERROR_MSG = \\"Error: File \\\\\\"{}\\\\\\"\\" not found.\\"
    SUCCESS_MSG = \\'Operation completed successfully!\\'
    MULTILINE_MSG = \\"\\"\\"\\"This is a multiline
    message with \\\\\\"quotes\\\\\\" and
    special characters: \\\\\\\\n\\\\\\\\t\\\\\\\\r\\"\\"\\"\\"

# Dictionary with escaped strings
config = {
    \\'database_url\\': \\'postgresql://user:pass@localhost/db\\',
    \\'error_template\\': \\'Error on line {}: \\"{}\\"\\',
    \\'file_pattern\\': r\\'\\\\\\\\w+\\\\\\\\.(py|txt|json)$\\'
}`);
  };

  const containerClass = isFullscreen 
    ? "fixed inset-0 z-50 bg-gray-900" 
    : "min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-green-900";

  return (
    <div className={containerClass}>
      <div className="container mx-auto p-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-blue-500 to-green-600 rounded-lg">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Python Escape/Unescape Tool</h1>
              <p className="text-gray-300 text-sm">Advanced Python string processor with Unicode and raw string support</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mode Toggle */}
        <div className="flex items-center justify-center mb-6">
          <div className="bg-gray-800 p-1 rounded-lg flex">
            <button
              onClick={() => setMode('escape')}
              className={`px-6 py-2 rounded-md transition-all flex items-center space-x-2 ${
                mode === 'escape' 
                  ? 'bg-blue-600 text-white shadow-lg' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              <Zap className="w-4 h-4" />
              <span>Escape Python</span>
            </button>
            <button
              onClick={() => setMode('unescape')}
              className={`px-6 py-2 rounded-md transition-all flex items-center space-x-2 ${
                mode === 'unescape' 
                  ? 'bg-green-600 text-white shadow-lg' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              <RotateCcw className="w-4 h-4" />
              <span>Unescape Python</span>
            </button>
          </div>
        </div>

        {/* Analytics Panel */}
        {showAnalytics && (
          <div className="mb-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              Processing Analytics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-blue-400">{stats.charactersProcessed.toLocaleString()}</div>
                <div className="text-gray-300 text-sm">Characters</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-green-400">{stats.entitiesFound}</div>
                <div className="text-gray-300 text-sm">Escapes</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-yellow-400">{stats.processingTime.toFixed(2)}ms</div>
                <div className="text-gray-300 text-sm">Process Time</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-purple-400">{stats.operationsCount}</div>
                <div className="text-gray-300 text-sm">Operations</div>
              </div>
            </div>
            
            {/* Python Escape Info */}
            <div className="mt-4 p-3 bg-gray-700 rounded-lg">
              <h4 className="text-white font-medium mb-2">Python Escape Sequences & Features</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-sm">
                <div className="text-gray-300">
                  <strong>Basic:</strong> \\\\ \\" \\' \\n \\r \\t
                </div>
                <div className="text-gray-300">
                  <strong>Control:</strong> \\a \\b \\f \\v \\0
                </div>
                <div className="text-gray-300">
                  <strong>Numeric:</strong> \\xXX \\ooo
                </div>
                <div className="text-gray-300">
                  <strong>Unicode:</strong> \\uXXXX \\UXXXXXXXX
                </div>
                <div className="text-gray-300">
                  <strong>Named:</strong> \\N{'{name}'} sequences
                </div>
                <div className="text-gray-300">
                  <strong>Raw:</strong> r"string" support
                </div>
                <div className="text-gray-300">
                  <strong>F-strings:</strong> Modern formatting
                </div>
                <div className="text-gray-300">
                  <strong>Bytes:</strong> b"string" literals
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content - Large input/output areas */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-[600px]">
          {/* Input Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Input</h2>
              <div className="flex space-x-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".py,.pyw,.txt,.json"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <div className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors">
                    <Upload className="w-4 h-4" />
                  </div>
                </label>
                <button
                  onClick={clearAll}
                  className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder={mode === 'escape' ? 'Enter Python code to escape...' : 'Enter escaped Python code to unescape...'}
              className="flex-1 min-h-[500px] bg-gray-900 text-white p-4 rounded-lg border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-none font-mono text-sm leading-relaxed"
            />
            
            <div className="mt-2 flex justify-between text-gray-400 text-sm">
              <span>Characters: {inputText.length.toLocaleString()}</span>
              <span>Lines: {inputText.split('\n').length}</span>
            </div>
          </div>

          {/* Output Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Output</h2>
              <div className="flex space-x-2">
                <button
                  onClick={copyToClipboard}
                  disabled={!outputText}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={downloadFile}
                  disabled={!outputText}
                  className="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <textarea
              value={outputText}
              readOnly
              placeholder="Processed Python output will appear here..."
              className="flex-1 min-h-[500px] bg-gray-900 text-white p-4 rounded-lg border border-gray-600 resize-none font-mono text-sm leading-relaxed"
            />
            
            <div className="mt-2 flex justify-between text-gray-400 text-sm">
              <span>Characters: {outputText.length.toLocaleString()}</span>
              <span>Lines: {outputText.split('\n').length}</span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={loadSamplePython}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Load Sample Python
            </button>
            <button
              onClick={loadSampleEscaped}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Load Sample Escaped
            </button>
            <button
              onClick={() => setMode(mode === 'escape' ? 'unescape' : 'escape')}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm"
            >
              Switch Mode
            </button>
            <button
              onClick={() => setInputText(inputText.replace(/#.*$/gm, '').trim())}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm"
            >
              Remove Comments
            </button>
            <button
              onClick={() => setInputText(inputText.replace(/\s+/g, ' ').trim())}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
            >
              Normalize Whitespace
            </button>
            <button
              onClick={() => setInputText(inputText.replace(/r"([^"]*)"/g, '"$1"').replace(/r'([^']*)'/g, "'$1'"))}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors text-sm"
            >
              Convert Raw Strings
            </button>
            <button
              onClick={() => setInputText(inputText.replace(/f"([^"]*)"/g, '"$1"').replace(/f'([^']*)'/g, "'$1'"))}
              className="px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white rounded-lg transition-colors text-sm"
            >
              Convert F-Strings
            </button>
            <button
              onClick={() => setInputText(inputText.replace(/b"([^"]*)"/g, '"$1"').replace(/b'([^']*)'/g, "'$1'"))}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors text-sm"
            >
              Convert Byte Strings
            </button>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50">
            <CheckCircle2 className="w-4 h-4" />
            <span>{notification}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PythonEscapeUnescapeTool;